import { ReactNode, useCallback, useEffect, useState } from "react";
import { IAircraft, IOCAPIError, IAircraftGroup } from "@vmosolutions/oc-interfaces";
import { Formik, Form, FormikProps, FormikHelpers } from "formik";
import { FormikTextField } from "../../../MUI5FormikComponents/FormikWrappers";
import { Box, Grid, Button, Typography, ButtonGroup, Paper } from "@mui/material";
import * as Yup from "yup";
import AmplifyAPIWrapper, { INotifications } from "../../../../API/AmplifyAPIWrapper";
import { useDispatch } from "react-redux";
import { IActiveModal } from "../../../../pages/Admin/AdminWindow";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import _ from "lodash";
import { DraggableListItem } from "src/components/DraggableListItem/DraggableListItem";

interface IAircraftGroupProps {
  selectedGroup?: IAircraftGroup;
  updateAircraftGroupList: (aircraft: IAircraftGroup, mode: "create" | "update" | "delete") => void;
  setActiveModal: (activeModal: IActiveModal | null) => void;
  aircraft: IAircraft[];
  setSelectedGroup: any;
}

const initialGroupValues: IAircraftGroup = {
  _id: undefined,
  title: "",
  members: [],
  createdBy: "",
  createdAt: 0,
};

type AircraftGroupFormType = FormikProps<IAircraftGroup>;

const AircraftGroupSchema = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  members: Yup.array().of(Yup.string()),
});

export const AircraftGroupForm = (props: IAircraftGroupProps): JSX.Element => {
  const dispatch = useDispatch();
  const { selectedGroup, updateAircraftGroupList, setActiveModal, aircraft, setSelectedGroup } = props;
  const initialValues = selectedGroup ? selectedGroup : initialGroupValues;
  const [groupIdListsInitialized, setGroupIdListsInitialized] = useState<string | null>(null);
  const [aircraftInGroup, setAircraftInGroup] = useState<IAircraft[]>([]);
  const [aircraftOutOfGroup, setAircraftOutOfGroup] = useState<IAircraft[]>([]);

  const populateAircraftFromIDs = useCallback(
    (aircraftIDs: string[]): IAircraft[] => {
      const fullAircraftGroup: IAircraft[] = [];
      aircraftIDs.forEach((id: string) => {
        const fullAC = aircraft.find((ac) => ac._id === id);
        if (fullAC) fullAircraftGroup.push(fullAC);
      });
      return fullAircraftGroup;
    },
    [aircraft]
  );

  const filterAircraft = useCallback(
    (airCraftList: IAircraft[], filterOpposite = false): IAircraft[] => {
      const filteredList = aircraft.filter((el) =>
        filterOpposite
          ? airCraftList.map((fa) => fa._id)?.includes(el._id)
          : !airCraftList.map((fa) => fa._id)?.includes(el._id)
      );
      return filteredList;
    },
    [aircraft]
  );

  const setAirCraftLists = useCallback(() => {
    const allAircraft: IAircraft[] = selectedGroup ? populateAircraftFromIDs(selectedGroup?.members) : aircraft;
    const acInGroupUnsorted: IAircraft[] = filterAircraft(allAircraft, selectedGroup ? true : false);
    const acInGroup = selectedGroup?.members
      ? selectedGroup.members.map((ac) => acInGroupUnsorted.find((acu) => acu._id === ac))
      : acInGroupUnsorted;
    const acOutOfGroup: IAircraft[] = filterAircraft(allAircraft, selectedGroup ? false : true);
    setAircraftOutOfGroup(acOutOfGroup);
    setAircraftInGroup(acInGroup);
  }, [aircraft, filterAircraft, populateAircraftFromIDs, selectedGroup]);

  useEffect(() => {
    setAirCraftLists();
  }, [setAirCraftLists]);

  const handleSubmit = useCallback(
    async (values: IAircraftGroup, helpers: FormikHelpers<IAircraftGroup>): Promise<void> => {
      const { setSubmitting, setStatus, resetForm, setFieldValue } = helpers; //setErrors
      const mode = values._id ? "update" : "create";
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: `${mode === "update" ? "Updating" : "Creating New"} Aircraft Group...`,
        successMessage: `Aircraft Group Successfully ${mode === "update" ? "Updated" : "Created"}.`,
        errorMessage: `Error: Unable to ${mode === "update" ? "Update" : "Create"} Aircraft Group`,
      };
      const response =
        mode === "create"
          ? await AmplifyAPIWrapper.Post<IAircraftGroup>("/aircraftGroup", values, notifications)
          : await AmplifyAPIWrapper.Put<IAircraftGroup>(`/aircraftGroup/${values._id}`, values, notifications);
      if (response?._id) {
        updateAircraftGroupList(response, mode);
        setSelectedGroup(response);
      } else {
        setStatus({ success: false });
      }
      setSubmitting(false);
    },
    [dispatch, setSelectedGroup, updateAircraftGroupList]
  );

  return (
    <Grid>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={handleSubmit}
        validationSchema={AircraftGroupSchema}
      >
        {(aircraftGroupForm: AircraftGroupFormType) => {
          const { dirty, isValid, isSubmitting, handleReset } = aircraftGroupForm;

          const handleDelete = (): void => {
            if (!selectedGroup?._id) {
              return;
            }

            setActiveModal({
              title: "Delete Aircraft Group",
              message: "Are you certain you wish to delete this aircraft group? This operation cannot be undone.",
              cancel: () => setActiveModal(null),
              confirm: async (): Promise<void> => {
                setActiveModal(null);
                const notifications: INotifications = {
                  dispatch: dispatch,
                  infoMessage: "Deleting Selected Aircraft Group...",
                  successMessage: "Aircraft Group Deleted",
                  errorMessage: "Error: Unable To Delete Aircraft Group",
                };
                const response: IOCAPIError | null = await AmplifyAPIWrapper.Del(
                  `/aircraftGroup/${selectedGroup._id}`,
                  {},
                  notifications
                );
                if (!response?.error) {
                  aircraftGroupForm.resetForm({ values: initialGroupValues });
                  aircraftGroupForm.setStatus({ success: true });
                  updateAircraftGroupList(selectedGroup, "delete");
                } else {
                  aircraftGroupForm.setStatus({ success: false });
                }
                aircraftGroupForm.setSubmitting(false);
              },
              confirmLabel: "Delete",
            });
          };

          const onDragEnd = (result: any): void => {
            const { source, destination } = result;
            if (!destination) {
              return;
            }
            if (source.droppableId === destination.droppableId) {
              const list = source.droppableId === "aircraftOutOfGroup" ? aircraftOutOfGroup : aircraftInGroup;
              const updatedList = Array.from(list);
              const [removedItem] = updatedList.splice(source.index, 1);
              updatedList.splice(destination.index, 0, removedItem);

              if (source.droppableId === "aircraftOutOfGroup") {
                setAircraftOutOfGroup(updatedList);
              } else {
                setAircraftInGroup(updatedList);
                aircraftGroupForm.setFieldValue(
                  "members",
                  updatedList.map((ac) => ac._id)
                );
              }
            } else {
              // Move between lists
              const sourceList = source.droppableId === "aircraftOutOfGroup" ? aircraftOutOfGroup : aircraftInGroup;
              const destinationList =
                destination.droppableId === "aircraftOutOfGroup" ? aircraftOutOfGroup : aircraftInGroup;
              const updatedSourceList = Array.from(sourceList);
              const updatedDestinationList = Array.from(destinationList);
              const [movedItem] = updatedSourceList.splice(source.index, 1);
              updatedDestinationList.splice(destination.index, 0, movedItem);
              const newAircraftInGroup =
                destination.droppableId === "aircraftInGroup" ? updatedDestinationList : updatedSourceList;
              const newAircraftOuOfGroup =
                source.droppableId === "aircraftOutOfGroup" ? updatedSourceList : updatedDestinationList;
              setAircraftOutOfGroup(newAircraftOuOfGroup);
              setAircraftInGroup(newAircraftInGroup);

              aircraftGroupForm.setFieldValue(
                "members",
                newAircraftInGroup.map((ac) => ac._id)
              );
            }
          };

          const subheaderStyles = {
            textTransform: "uppercase",
            fontSize: "0.9em",
          };

          return (
            <Form>
              <Grid container>
                <Grid container item spacing={3}>
                  <Grid item xs={12} sx={{ mt: 3 }}>
                    <FormikTextField label="Name" name="title" />
                  </Grid>
                  <DragDropContext onDragEnd={(result) => onDragEnd(result)}>
                    <Grid item xs={6}>
                      <Typography component="h6" sx={subheaderStyles}>
                        Available Aircraft ({aircraftOutOfGroup.length})
                      </Typography>
                      <Box sx={{ maxHeight: "370px", overflow: "scroll" }}>
                        <Droppable droppableId="aircraftOutOfGroup" isDropDisabled={false}>
                          {(provided, snapshot) => (
                            <Paper ref={provided.innerRef} {...provided.droppableProps} sx={{ minHeight: "360px" }}>
                              {aircraftOutOfGroup.map((ac, idx) => (
                                <Draggable key={ac._id} draggableId={ac._id} index={idx}>
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                    >
                                      <DraggableListItem active={ac?.isActive ? true : false}>
                                        {`${ac.registration} ${!ac.isActive ? "(INACTIVE)" : ""}`}
                                      </DraggableListItem>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder as ReactNode}
                            </Paper>
                          )}
                        </Droppable>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography component="h6" sx={subheaderStyles}>
                        Aircraft In Group({aircraftInGroup.length})
                      </Typography>
                      <Box sx={{ maxHeight: "370px", overflow: "scroll" }}>
                        <Droppable droppableId="aircraftInGroup" isDropDisabled={false}>
                          {(provided, snapshot) => (
                            <Paper ref={provided.innerRef} {...provided.droppableProps} style={{ minHeight: "360px" }}>
                              {aircraftInGroup.map((ac: any, idx: number) => (
                                <Draggable key={ac._id} draggableId={ac._id} index={idx}>
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                    >
                                      <DraggableListItem active={ac?.isActive ? true : false}>
                                        {`${ac.registration} ${!ac.isActive ? "(INACTIVE)" : ""}`}
                                      </DraggableListItem>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder as ReactNode}
                            </Paper>
                          )}
                        </Droppable>
                      </Box>
                    </Grid>
                  </DragDropContext>
                </Grid>
                <Grid item justifyContent={"center"} xs={12}>
                  <ButtonGroup variant="contained">
                    <Button color="success" disabled={isSubmitting || !isValid || !dirty} type="submit">
                      {selectedGroup ? "Update" : "Create"}
                    </Button>
                    <Button
                      type="reset"
                      color="secondary"
                      disabled={(!dirty && isValid) || isSubmitting}
                      onClick={() => {
                        aircraftGroupForm.setFieldValue("members", selectedGroup?.members);
                        setAirCraftLists();
                        handleReset();
                      }}
                    >
                      Reset
                    </Button>
                    {selectedGroup && (
                      <Button color="error" disabled={!selectedGroup || isSubmitting} onClick={handleDelete}>
                        Delete
                      </Button>
                    )}
                  </ButtonGroup>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </Grid>
  );
};
